/**
 * WorkloadTrainabilityChip — NC-1296 Soll 4.
 *
 * States on the workload card whether it can carry a training, and when it
 * cannot, **why** — every concrete reason, in the tooltip, before anyone opens
 * the run dialog and hits a 400 (the #1273 dead-end class).
 *
 * "Schulbar" is deliberately shown too, not only the failures: without it the
 * absence of a chip is ambiguous between "fine" and "not evaluated".
 *
 * The chip renders reasons as plain text lines rather than a list, because MUI's
 * Tooltip title takes a node but the test mock flattens it — keeping it textual
 * means what the test asserts is what a user reads.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  CheckCircleOutline as ReadyIcon,
  ErrorOutline as BlockedIcon,
  WarningAmber as IncompleteIcon,
} from '@mui/icons-material';
import { WorkloadDefinition } from '../../services/types';
import { evaluateTrainability, TrainabilityStatus } from '../../utils/workloadTrainability';

interface Props {
  workload: WorkloadDefinition;
}

const COLOR: Record<TrainabilityStatus, 'success' | 'error' | 'warning'> = {
  ready: 'success',
  blocked: 'error',
  incomplete: 'warning',
};

const READY_TOOLTIP =
  'Konfiguration vollständig und Bereitstellung möglich. Ob aktuell ein Image veröffentlicht ist, zeigt die Image-Builds-Seite.';

export const WorkloadTrainabilityChip: React.FC<Props> = ({ workload }) => {
  const result = evaluateTrainability(workload);

  const title =
    result.status === 'ready'
      ? READY_TOOLTIP
      : [
          result.hasHardBlocker
            ? 'Nicht schulbar — die Gründe lassen sich nicht alle im Editor beheben:'
            : 'Noch unvollständig:',
          ...result.reasons.map((r) => `• ${r.text}${r.hint ? ` (${r.hint})` : ''}`),
        ].join('\n');

  const Icon =
    result.status === 'ready' ? ReadyIcon : result.status === 'blocked' ? BlockedIcon : IncompleteIcon;

  return (
    <Tooltip title={title}>
      <Chip
        label={result.label}
        size="small"
        color={COLOR[result.status]}
        variant={result.status === 'ready' ? 'outlined' : 'filled'}
        icon={<Icon />}
        data-testid={`workload-trainability-${result.status}`}
      />
    </Tooltip>
  );
};

export default WorkloadTrainabilityChip;
